import { useHookstate } from '@hookstate/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import ArrowRightWithBody from '../../../assets/icons/ArrowRightWithBody';
import DownloadGrayBg from '../../../assets/icons/DownloadGrayBg';
import Eye from '../../../assets/icons/Eye';
import EyeGrayBg from '../../../assets/icons/EyeGrayBg';
import Edit from '../../../assets/icons/Edit';
import TrashRed from '../../../assets/icons/TrashRed';
import { FilePDF } from '../../../assets/icons/FilePDF';
import { Avatar } from '../../../components/Avatar';
import Modal from '../../../components/Modal/Modal';
import DefaultButton from '../../../components/default-button/DefaultButton';
import { DisciplinaryDemerit } from '../../../components/disciplinary-table/DisciplinaryDemerit';
import InputField from '../../../components/input-field/InputField';
import { useSocialTranscriptScore } from '../../../hooks/user';
import { demeritStatusMapper, psychologistAppointmentFormats, socialTranscriptExcel, userDisplayData, userPsychologistClientsTableColumnns, userSocialTranscriptItemsTableColumns, userTabs } from '../../../misc/constants';
import { getDateOnly, getHHMMFromDateString, onInputChangeNative, socialTranscriptItemsReduce, updateLocalStateRaw, showSnackbar, userHasRole, exportSocialTranscriptToDocx } from '../../../misc/utils';
import { Demerit, MainUserWithDemeritsAndSocialTranscriptItems, Media } from '../../../types';
import { useUsersWithSocialScoreAndDemerits } from '../../../hooks/demerit-typed';
import { useDebounce } from '../../../hooks/ui';
import { HasRole } from '../../../components/HasRole';
import { demeritsApi } from '../../../api/api';
import { simpleGetter } from '../../../actions/common';
import { useGlobalUserState } from '../../../stores/user';
import { Download } from '../../../assets/icons/Download';

const UserDemerits = ({ selectedStudent }: { selectedStudent: MainUserWithDemeritsAndSocialTranscriptItems | null }) => {
    const localState = useHookstate<DisciplinaryTableStudentsState>({
        files: {}
    });
    const state: Record<string, any> = localState.get();

    const isAddDemeritModalOpen = Boolean(state.isAddDemeritModalOpen);
    const userState = useGlobalUserState();
    const uniqUser = userState.get().uniqUser || {};
    const openDemeritView = (demerit: Demerit) => () => {
        localState.merge({
            selectedDemerit: demerit,
            demeritModalMode: 'view',
            isAddDemeritModalOpen: true
        })
    }
    const onDeleteDemerit = (demerit: Demerit) => async () => {
        if (!window.confirm('Удалить этот выговор?')) return;
        const response = await simpleGetter(demeritsApi.deleteDemerit, demerit.id);
        showSnackbar(response.success ? 'Удалено' : 'Не удалось удалить', response.success ? 'success' : 'error');
        if (response.success) {
            localState.merge({
                studentsWithDemeritsTriggerFetch: (state.studentsWithDemeritsTriggerFetch || 0) + 1
            })
        }
    }
    return (
        <table className="w-full border-collapse mt-4 rounded-lg overflow-hidden">
            <thead>
                <tr className="bg-white">
                    <th className="px-4 py-2 border-b text-left">Тип выговора</th>
                    <th className="px-4 py-2 border-b text-left">
                        Дата создания
                    </th>
                    <th className="px-4 py-2 border-b text-left">Статус</th>
                    <th className="px-4 py-2 border-b text-left"></th>
                </tr>
            </thead>
            <tbody>
                {selectedStudent?.demerits.map((warning, index) => (
                    <tr key={index} className="border-b">
                        <td className="px-4 py-2">{warning.demerit_type.name}</td>
                        <td className="px-4 py-2">{getDateOnly(warning.createdAt)}</td>
                        <td className="px-4 py-2">
                            <span
                            // className={`font-semibold ${warning.status === 'Действует'
                            //   ? 'text-orange-500'
                            //   : warning.status === 'Завершён'
                            //     ? 'text-green-500'
                            //     : 'text-red-500'
                            //   }`}
                            >
                                {demeritStatusMapper[warning.status]}
                            </span>
                        </td>
                        <td className="px-4 py-2 max-w-[160px]">
                            <div className="flex gap-2">
                                <HasRole roles={['users_view_demerits']}>
                                    <DefaultButton
                                        Icon={Eye}
                                        onClick={openDemeritView(warning)}
                                        additionalStyle="p-2 border border-gray-400 text-gray-400 hover:bg-gray-100"
                                    />
                                </HasRole>
                                {(userHasRole('view_demerits_extended') || userHasRole('create_demerits') || warning.created_by === uniqUser.id) && (
                                    <DefaultButton
                                        Icon={Edit}
                                        disabled
                                        additionalStyle="p-2 border border-gray-200 text-gray-300 cursor-not-allowed"
                                    />
                                )}
                                {(userHasRole('view_demerits_extended') || warning.created_by === uniqUser.id) && (
                                    <DefaultButton
                                        Icon={TrashRed}
                                        onClick={onDeleteDemerit(warning)}
                                        additionalStyle="p-2 border border-gray-400 text-red-500 hover:bg-gray-100"
                                    />
                                )}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
            <Modal
                additionalClassName="w-[52%] h-[90%] rounded-xl p-8 flex flex-col"
                visible={isAddDemeritModalOpen}
                setVisible={updateLocalStateRaw(localState, 'isAddDemeritModalOpen')}
            >
                <DisciplinaryDemerit parentState={localState} selectedStudent={selectedStudent} mode={state.demeritModalMode || 'view'} />
            </Modal>
        </table>
    )
}

const UserSocialTranscriptItems = ({ selectedStudent }: { selectedStudent: MainUserWithDemeritsAndSocialTranscriptItems | null }) => {
    const onDownloadFile = (media: Media) => () => {
        const link = document.createElement("a");
        link.href = media.link;
        link.setAttribute("download", media.filename || "");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    return (
        <table className="w-full border-collapse mt-4 rounded-lg overflow-hidden">
            <thead>
                <tr>
                    {userSocialTranscriptItemsTableColumns.map(c => (
                        <th className="px-4 py-2 border-b text-left">{c}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {selectedStudent?.social_transcript_items.map(item => {
                    const media = item.media[0] || {}
                    return (
                        <tr className='border-b border-valley-background-blue'>
                            <td className='py-3'>
                                {item.social_subcategory.social_category?.name}
                            </td>
                            <td className='py-3'>
                                {item.social_subcategory.name}
                            </td>
                            <td className='py-3'>
                                {getDateOnly(item.createdAt)}
                            </td>
                            <td className='py-3'>
                                <div className="flex items-center justify-between border rounded-lg border-[#F0F3FA] px-2 py-[6px]">
                                    <div className='flex flex-row items-center gap-3'>
                                        <FilePDF />
                                        <p className="text-black">{media.filename || media.link}</p>
                                    </div>
                                    <div className='flex flex-row gap-3'>
                                        <a href={media.link} target="_blank" rel="noreferrer" className="p-0 bg-transparent">
                                            <EyeGrayBg />
                                        </a>
                                        <button onClick={onDownloadFile(media)} className="p-0 bg-transparent">
                                            <DownloadGrayBg />
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

export const UserPsychologistClients = ({ selectedStudent }: { selectedStudent: MainUserWithDemeritsAndSocialTranscriptItems | null }) => {
    return (
        <table className="w-full border-collapse mt-4 rounded-lg overflow-hidden">
            <thead>
                <tr>
                    {userPsychologistClientsTableColumnns.map(c => (
                        <th className="px-4 py-2 border-b text-left">{c}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {selectedStudent?.psychologist_clients?.map(item => {
                    const foundFormat = psychologistAppointmentFormats.find(f => f.value === item.appointment.format) || psychologistAppointmentFormats[0];
                    const Icon = foundFormat.Icon;
                    return (
                        <tr className='border-b border-valley-background-blue'>
                            <td className='py-3'>
                                <div className="flex items-center gap-2">
                                    <Avatar user={item.appointment.psychologist} />
                                    <span>
                                        {item.appointment.psychologist.json_saved_data.firstname} {item.appointment.psychologist.json_saved_data.lastname}
                                    </span>
                                </div>
                            </td>
                            <td className='py-3'>
                                {getDateOnly(item.appointment.start_time)}
                            </td>
                            <td className='py-3'>
                                {getHHMMFromDateString(item.appointment.start_time)}-{getHHMMFromDateString(item.appointment.end_time)}
                            </td>
                            <td className='py-3'>
                                <div className='flex flex-row items-center gap-2'>
                                    <Icon />
                                    <span className={`font-medium text-[15px] leading-[19.5px] ${foundFormat.textClassName}`}>
                                        {foundFormat.label}
                                    </span>
                                </div>
                            </td>
                            <td className='py-3'>

                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

const categoryList = [
    {
        label: '',
        additionalClassName: 'text-left'
    },
    {
        label: 'Студент',
        additionalClassName: 'text-left'
    },
    {
        label: 'Социальный транскрипт',
        additionalClassName: 'text-right'
    },
    {
        label: 'Оценка комнаты',
        additionalClassName: 'text-left'
    },
    {
        label: 'Количество нарушений',
        additionalClassName: 'text-left'
    },
    {
        label: 'Почта',
        additionalClassName: 'text-left'
    },
    // {
    //   label: 'Пол',
    //   additionalClassName: 'text-left'
    // },
]

export type DisciplinaryTableStudentsState = {
    files: Record<string, File | null>,
    currentTab?: string,
    search?: string;
    isAddDemeritModalOpen?: boolean;
    demeritModalMode?: string;
    demeritDescription?: string;
    demeritTypeSelected?: string;
    demeritIncidentLink?: string;
    selectedStuden?: number;
    studentsWithDemeritsTriggerFetch?: number;
    selectedDemerit?: Demerit;
    page?: number;
    usersList?: MainUserWithDemeritsAndSocialTranscriptItems[];
    loading?: boolean;
    total?: number;
}

export const Users = () => {
    const localState = useHookstate<DisciplinaryTableStudentsState>({
        files: {},
        currentTab: 'demerits',
        page: 0,
        loading: false,
        total: 0
    });
    const usersListRef = useRef<HTMLDivElement>(null);
    const state = localState.get();
    const search = state.search || "";
    const debouncedQuery = useDebounce(search, 1000);
    const currentTab = state.currentTab;
    const page = state.page || 0;
    const isAddDemeritModalOpen = Boolean(state.isAddDemeritModalOpen);
    const studentsWithDemeritsTriggerFetch = state.studentsWithDemeritsTriggerFetch || 0;
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [selectedStudent, setSelectedStudent] =
        useState<MainUserWithDemeritsAndSocialTranscriptItems | null>(null);
    const [score, merits] = useSocialTranscriptScore(selectedStudent?.id);

    useEffect(() => {
        if (isAddDemeritModalOpen) {
            localState.files.set({})
        }
    }, [isAddDemeritModalOpen])

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        localState.merge({
            search: e.target.value,
            page: 0
        })
    }

    const postUpdateStudentsWithDemerits = (newList: MainUserWithDemeritsAndSocialTranscriptItems[]) => {
        localState.usersList.set(newList);
        const found = newList.find((item: MainUserWithDemeritsAndSocialTranscriptItems) => item.id === selectedStudent?.id)
        if (found) {
            setSelectedStudent(found)
        }
    }

    const updateTab = (key: string) => () => {
        updateLocalStateRaw(localState, 'currentTab')(key)
    }

    //const allStudentsWithDemerits: MainUserWithDemeritsAndSocialTranscriptItems[] = useUsersWithDemerits(studentsWithDemeritsTriggerFetch, postUpdateStudentsWithDemerits)

    const [allStudentsWithDemerits, allStudentsLoading, allStudentsTotal] =
        useUsersWithSocialScoreAndDemerits<"demerits">(studentsWithDemeritsTriggerFetch, {
            page,
            type: 'demerits',
            usersWhere: {
                query: debouncedQuery
            }
        }, postUpdateStudentsWithDemerits)

    useEffect(() => {
        localState.merge({
            loading: allStudentsLoading,
            total: allStudentsTotal
        })
    }, [allStudentsLoading, allStudentsTotal])

    const studentsWithDemerits = allStudentsWithDemerits/*.filter(d => d.json_saved_data.email.toLowerCase().includes(search.toLowerCase()) || d.json_saved_data?.firstname?.toLowerCase().includes(search.toLowerCase()) || d.json_saved_data?.lastname?.toLowerCase().includes(search.toLowerCase()))*/

    const handleArrowClick = (student: MainUserWithDemeritsAndSocialTranscriptItems) => {
        if (selectedStudent?.id === student.id) {
            setIsCollapsed(false);
            setSelectedStudent(null);
        } else {
            setSelectedStudent(student);
            setIsCollapsed(true);
        }
    };

    const handleScroll = useCallback(() => {
        if (usersListRef.current) {
            const stateLocal = localState.get()
            const usersList = stateLocal.usersList || []
            const total = stateLocal.total || 0
            const loading = stateLocal.loading || false
            const { scrollTop, scrollHeight, clientHeight } = usersListRef.current;
            if (scrollHeight - scrollTop <= clientHeight * 1 && usersList.length < total && !loading) {
                localState.merge({
                    page: (stateLocal.page || 0) + 1
                })
            }
        }
    }, [])

    useEffect(() => {
        if (usersListRef.current) {
            usersListRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (usersListRef.current) {
                usersListRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [])

    return (
        <div className="bg-[#f8f9fd] h-full text-black p-5 text-sm overflow-y-auto">
            <div className="bg-[#f8f9fd] flex gap-5 h-full">
                <div
                    className={`${isCollapsed ? 'w-1/4' : 'w-full'
                        } transition-all bg-white p-4 rounded-lg overflow-y-auto`}
                    ref={usersListRef}
                >
                    <div className="flex justify-between">
                        <InputField value={search} handleChange={onSearchChange} type="text" placeholder="Поиск по студентам" />
                    </div>

                    <table className="w-full border-collapse mt-4 rounded-lg overflow-hidden text-md">
                        <thead>
                            <tr className="bg-white">
                                {categoryList.map((category, index) => (
                                    <th
                                        key={index}
                                        className={`px-4 py-2 border-b ${category.additionalClassName} ${isCollapsed && index > 1 ? 'hidden' : ''
                                            }`}
                                    >
                                        {category.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {studentsWithDemerits.map((item, index) => (
                                <tr
                                    key={index}
                                    className="border-b border-gray-200 bg-white-500 "
                                >
                                    <td className="px-4 py-2 bg-white max-w-[50px] align-center">
                                        <DefaultButton
                                            onClick={() => handleArrowClick(item)}
                                            Icon={ArrowRightWithBody}
                                            additionalStyle="px-2 py-2 bg-blue-100 opacity-[0.7] border-none text-blue-500 font-bold"
                                            iconStyle={`transition-transform duration-300 opacity-[1] ${selectedStudent?.id === item.id
                                                ? 'rotate-180'
                                                : 'rotate-0'
                                                }`}
                                        />
                                    </td>
                                    <td className="px-4 py-2 bg-white align-center">
                                        <div className="flex items-center gap-2">
                                            <Avatar user={item} />
                                            <span>
                                                {item.json_saved_data.firstname} {item.json_saved_data.lastname}
                                            </span>
                                        </div>
                                    </td>
                                    {!isCollapsed && (
                                        <>
                                            <td className="px-4 py-2 bg-white text-end max-w-[100px]">
                                                {item.social_transcript_items.reduce(socialTranscriptItemsReduce, 0)}
                                            </td>
                                            <td className="px-4 py-2 bg-white">
                                                <p className="flex items-center gap-1 text-gray-400">
                                                    <span
                                                        className={`${0 > 3
                                                            ? 'text-green-500'
                                                            : 'text-red-500'
                                                            } text-lg`}
                                                    >
                                                        {0}
                                                    </span>{' '}
                                                    / 5
                                                </p>
                                            </td>
                                            <td className="px-4 py-2 bg-white">
                                                {item.demerits.length}
                                            </td>
                                            <td className="px-4 py-2 bg-white">{item.json_saved_data.email}</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {isCollapsed && selectedStudent && (
                    <div className="w-3/4 flex flex-col rounded-lg bg-[#f8f9fd] overflow-y-auto">
                        <div className='flex flex-row p-6 bg-white rounded-lg mb-6'>
                            <div className="flex-[38] flex flex-row items-center gap-4">
                                <Avatar user={selectedStudent} />
                                <div>
                                    <h2 className="text-2xl font-bold">
                                        {selectedStudent.json_saved_data.firstname} {selectedStudent.json_saved_data.lastname}
                                    </h2>
                                    <p className="text-gray-500">{selectedStudent.json_saved_data.email}</p>
                                </div>
                            </div>
                            <div className='flex-[62] flex flex-row'>
                                {userDisplayData.map(d => {
                                    const student = {
                                        selectedStudent, merits
                                    };
                                    const Component = d.Component;
                                    return (
                                        <div key={d.key} className='flex flex-col gap-[11px] justify-between flex-1 pl-4 pt-[3px] border-l border-valley-background-blue'>
                                            <span className='font-medium text-sm leading-[15.4px] text-valley-gray'>
                                                {d.label}
                                            </span>
                                            {Component ? (
                                                <Component student={student} />
                                            ) : (
                                                <span className='font-medium text-[32px] leading-8 text-valley-main'>
                                                    {d.getData(student)} 
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 rounded-lg mb-6">
                            <div className="flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3">
                                <div className="flex items-start justify-between w-full">
                                    <span className="text-gray-500 font-medium">
                                        Социальный транскрипт
                                    </span>
                                    <DefaultButton
                                        Icon={Star}
                                        iconStyle="w-6 h-6"
                                        additionalStyle="border-none bg-transparent text-gray-400 py-0.5 px-1"
                                    />
                                </div>
                                <div className={'text-3xl font-bold mt-2 text-blue-500'}>
                                    {selectedStudent.social_transcript_items.reduce(socialTranscriptItemsReduce, 0)}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3">
                                <div className="flex items-start justify-between w-full">
                                    <span className="text-gray-500 font-medium">
                                        Средняя оценка
                                    </span>
                                    <DefaultButton
                                        Icon={Star}
                                        iconStyle="w-6 h-6"
                                        additionalStyle="border-none bg-transparent text-gray-400 py-0.5 px-1"
                                    />
                                </div>
                                <div className={'text-3xl font-bold mt-2 text-green-500'}>
                                    {0}
                                    <span className="text-[19px] font-normal text-gray-400">
                                        {' '}
                                        %
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3">
                                <div className="flex items-start justify-between w-full">
                                    <span className="text-gray-500 font-medium">
                                        Оценка комнаты
                                    </span>
                                    <DefaultButton
                                        Icon={Home}
                                        iconStyle="w-6 h-6"
                                        additionalStyle="border-none bg-transparent text-gray-400 py-0.5 px-1"
                                    />
                                </div>
                                <div className={'text-3xl font-bold mt-2 text-blue-500'}>
                                    {0}
                                    <span className="text-[19px] font-normal text-gray-400">
                                        {' '}
                                        / 5
                                    </span>
                                </div>
                            </div>
                        </div> */}
                        <div className='flex flex-col bg-white flex-1 p-4 rounded-lg gap-4'>
                            <div className='flex flex-row items-center relative'>
                                <div className="flex flex-row self-start bg-[#0068FF12] rounded-xl p-1 gap-3">
                                    {userTabs.map(t => {
                                        const isActive = currentTab === t.key;
                                        return (
                                            <div key={t.key} className={`flex flex-row ${isActive && 'bg-valley-main'} gap-[6px] px-2 py-1.5 items-center rounded-lg cursor-pointer`} onClick={updateTab(t.key)}>
                                                <span className={`font-medium leading-[19.2px] ${isActive ? 'text-white' : 'text-valley-main'}`}>
                                                    {t.label}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                                {currentTab === 'social-transcript' && selectedStudent && (
                                    <DefaultButton
                                        Icon={Download}
                                        buttonText="Выгрузить"
                                        onClick={() => {
                                            exportSocialTranscriptToDocx(
                                                selectedStudent,
                                                selectedStudent.social_transcript_items || [],
                                                score
                                            )();
                                        }}
                                        additionalStyle="absolute right-0 border-gray-500 text-gray-500 px-3 py-2 text-sm hover:bg-gray-100"
                                    />
                                )}
                            </div>
                            <div className='flex flex-col flex-1 overflow-y-auto'>
                                {currentTab === 'demerits' && <UserDemerits selectedStudent={selectedStudent} />}
                                {currentTab === 'social-transcript' && <UserSocialTranscriptItems selectedStudent={selectedStudent} />}
                                {currentTab === 'psychologist' && <UserPsychologistClients selectedStudent={selectedStudent} />}
                            </div>
                        </div>
                        {/* {Boolean(merits.length) && (
                            <div className="flex flex-col bg-white p-4 rounded-lg gap-4 mb-6">
                                {merits.map(m => (
                                    <div className='flex flex-row gap-4 items-center px-4 py-2' key={m.id}>
                                        <img className='w-10 h-10' src={m.media.link} alt={m.key} />
                                        <span className='text-xl font-medium text-valley-black'>
                                            {m.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {Boolean(selectedStudent.social_transcript_items?.length) && (
                            <div className="flex flex-col bg-white p-4 rounded-lg gap-4 mb-6">
                                <span className='text-xl font-semibold text-valley-black'>
                                    Загруженные файлы
                                </span>
                                {selectedStudent.social_transcript_items.map(item => {
                                    const media = item.media[0] || {}
                                    return (
                                        <div className="flex items-center justify-between border rounded-lg border-[#F0F3FA] px-2 py-[6px]">
                                            <div className='flex flex-row items-center gap-3'>
                                                <FilePDF />
                                                <p className="text-black">{media.filename || media.link}</p>
                                            </div>
                                            <div className='flex flex-row gap-3'>
                                                <a href={media.link} target="_blank" rel="noreferrer" className="p-0 bg-transparent">
                                                    <EyeGrayBg />
                                                </a>
                                                <button onClick={onDownloadFile(media)} className="p-0 bg-transparent">
                                                    <DownloadGrayBg />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )} */}
                    </div>
                )}
            </div>
        </div>
    );
}